import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../../SuperAdmin/Sidebar';
import Header from '../../SuperAdmin/Header';
import Cookies from "js-cookie";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { toast } from "react-hot-toast";
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiOutlineSearch,
  HiOutlineUser,
  HiOutlineMail,
  HiOutlineCalendar,
  HiOutlineClock,
  HiOutlineRefresh,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineUsers,
  HiOutlineBan,
  HiOutlineCheckCircle,
  HiOutlineXCircle
} from "react-icons/hi";
import { FaSortAlphaDown, FaSortAlphaUp, FaClock } from "react-icons/fa";

const UserManagement = () => {
  // ✅ Sidebar state and toggle
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // ✅ Original state
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('time');
  const [sortDirection, setSortDirection] = useState('desc');
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    blocked: 0
  });

  const itemsPerPage = 10;
  const authToken = Cookies.get("superAdminToken");
  const searchInputRef = useRef(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Sort users based on selected option
  const sortUsers = (usersToSort) => {
    if (sortOption === 'time') {
      return [...usersToSort].sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
    } else {
      return [...usersToSort].sort((a, b) => {
        const nameA = (a.fullName || '').toLowerCase();
        const nameB = (b.fullName || '').toLowerCase();
        
        if (sortDirection === 'asc') {
          return nameA.localeCompare(nameB);
        } else {
          return nameB.localeCompare(nameA);
        }
      });
    }
  };

  // Filter and sort users
  useEffect(() => {
    let filtered = users;
    
    if (debouncedSearchTerm.trim() !== '') {
      filtered = users.filter(user => 
        (user.fullName && user.fullName.toLowerCase().includes(debouncedSearchTerm.toLowerCase())) ||
        (user.email && user.email.toLowerCase().includes(debouncedSearchTerm.toLowerCase()))
      );
    }
    
    const sorted = sortUsers(filtered);
    setFilteredUsers(sorted);
    setCurrentPage(1);
  }, [debouncedSearchTerm, users, sortOption, sortDirection]);

  // Update stats when users change
  useEffect(() => {
    setStats({
      total: users.length,
      active: users.filter(u => !u.isBlocked).length,
      blocked: users.filter(u => u.isBlocked).length
    });
  }, [users]);

  const fetchUsers = async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);
      else setLoading(true);
      
      setError(null);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/apis/superadmin/getAllUser`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const dataa = await response.json();

      if (!response.ok) {
        throw new Error(dataa.message || `HTTP error! status: ${response.status}`);
      }

      if (dataa.success) {
        let usersData = [];
        if (dataa.data && Array.isArray(dataa.data.users)) {
          usersData = dataa.data.users;
        } else if (Array.isArray(dataa.data)) {
          usersData = dataa.data;
        } else if (Array.isArray(dataa.users)) {
          usersData = dataa.users;
        }
        
        setUsers(usersData);
        
        if (usersData.length > 0 && !showRefresh) {
          toast.success(`Loaded ${usersData.length} users`, {
            icon: '👥',
            duration: 2000
          });
        }
      } else {
        throw new Error(dataa.message || "Failed to fetch users");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setError(error.message);
      toast.error(error.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleToggleBlock = async (userId, currentStatus, userName) => {
    try {
      // Optimistic update
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user._id === userId ? { ...user, isBlocked: !currentStatus } : user
        )
      );

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/apis/superadmin/deleteUser/${userId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ isBlocked: !currentStatus }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update user status");
      }

      toast.success(
        <div>
          <p className="font-medium">{userName}</p>
          <p className="text-sm opacity-90">
            User {!currentStatus ? 'blocked' : 'unblocked'} successfully
          </p>
        </div>,
        {
          icon: !currentStatus ? '🔒' : '🔓',
          duration: 3000
        }
      );
    } catch (error) {
      console.error("Error updating user status:", error);
      // Revert optimistic update
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user._id === userId ? { ...user, isBlocked: currentStatus } : user
        )
      );
      toast.error(error.message || "Failed to update user status");
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `Today, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    }
    
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleSortDirection = () => {
    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const currentData = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const getStatusBadge = (isBlocked) => {
    return isBlocked ? (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700 border border-gray-300">
        <HiOutlineBan className="w-3 h-3 mr-1" />
        Blocked
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
        <HiOutlineCheckCircle className="w-3 h-3 mr-1" />
        Active
      </span>
    );
  };

  const StatsCard = ({ title, value, icon: Icon, subtitle }) => (
    <motion.div
      whileHover={{ y: -2 }}
      className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
          <Icon className="w-5 h-5 text-gray-600" />
        </div>
      </div>
    </motion.div>
  );

  const renderSkeletonRows = () => {
    return Array(5)
      .fill(0)
      .map((_, index) => (
        <tr key={`skeleton-${index}`} className="even:bg-gray-50 animate-pulse">
          <td className="px-6 py-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gray-200 rounded-full mr-3"></div>
              <Skeleton width={100} />
            </div>
          </td>
          <td className="px-6 py-4"><Skeleton width={150} /></td>
          <td className="px-6 py-4"><Skeleton width={70} /></td>
          <td className="px-6 py-4"><Skeleton width={120} /></td>
          <td className="px-6 py-4"><Skeleton width={120} /></td>
          <td className="px-6 py-4"><Skeleton width={80} /></td>
        </tr>
      ));
  };

  const renderSkeletonCards = () => {
    return Array(3).fill(0).map((_, i) => (
      <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <Skeleton width={120} height={20} />
            <Skeleton width={180} height={16} className="mt-2" />
          </div>
          <Skeleton width={60} height={24} />
        </div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <Skeleton height={30} />
          <Skeleton height={30} />
        </div>
        <div className="flex justify-end">
          <Skeleton width={80} height={24} />
        </div>
      </div>
    ));
  };

  const renderEmptyState = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center py-16 px-4"
    >
      <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
        <HiOutlineUsers className="w-10 h-10 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        {searchTerm ? 'No users found' : 'No users yet'}
      </h3>
      <p className="text-gray-500 mb-6 max-w-md mx-auto">
        {searchTerm 
          ? `No users matching "${searchTerm}"`
          : 'Users will appear here once they register on the platform'}
      </p>
      {searchTerm && (
        <button
          onClick={() => {
            setSearchTerm('');
            searchInputRef.current?.focus();
          }}
          className="inline-flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors"
        >
          Clear Search
        </button>
      )}
    </motion.div>
  );

  if (loading) {
    return (
      <div className="flex h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 overflow-hidden">
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header title="User Management" toggleSidebar={toggleSidebar} />
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-gray-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 overflow-hidden">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="User Management" toggleSidebar={toggleSidebar} />
        
        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="p-4 lg:p-6"
          >
            <div className="bg-white rounded-xl shadow-lg p-4 lg:p-6 border border-gray-200">
              {/* Header with Stats */}
              <div className="mb-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                  <div>
                    <h1 className="text-2xl lg:text-3xl font-bold text-gray-800">User Management</h1>
                    <p className="text-sm text-gray-500 mt-1">
                      {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} found
                      {searchTerm && ` for "${searchTerm}"`}
                    </p>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                    {/* Search Bar */}
                    <div className="relative flex-1 sm:min-w-[300px]">
                      <HiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search by name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white text-gray-700 transition-shadow"
                      />
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm('')}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <HiOutlineXCircle className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      {/* Sort Options */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSortOption('time')}
                          className={`flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg transition-all ${
                            sortOption === 'time'
                              ? 'bg-gray-700 text-white shadow-md'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          title="Sort by registration date"
                        >
                          <FaClock className="w-4 h-4" />
                        </button>
                        
                        <button
                          onClick={() => setSortOption('alphabetical')}
                          className={`flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg transition-all ${
                            sortOption === 'alphabetical'
                              ? 'bg-gray-700 text-white shadow-md'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          title="Sort alphabetically"
                        >
                          {sortDirection === 'asc' ? (
                            <FaSortAlphaDown className="w-4 h-4" />
                          ) : (
                            <FaSortAlphaUp className="w-4 h-4" />
                          )}
                        </button>
                        
                        {sortOption === 'alphabetical' && (
                          <button
                            onClick={toggleSortDirection}
                            className="px-3 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                            title={sortDirection === 'asc' ? 'A to Z' : 'Z to A'}
                          >
                            {sortDirection === 'asc' ? 'A→Z' : 'Z→A'}
                          </button>
                        )}
                      </div>

                      <button
                        onClick={() => fetchUsers(true)}
                        disabled={refreshing}
                        className="p-2.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                        title="Refresh"
                      >
                        <HiOutlineRefresh className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Stats Cards */}
                {users.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                    <StatsCard title="Total Users" value={stats.total} icon={HiOutlineUsers} />
                    <StatsCard title="Active" value={stats.active} icon={HiOutlineCheckCircle} subtitle={`${stats.active > 0 ? Math.round((stats.active / stats.total) * 100) : 0}% of total`} />
                    <StatsCard title="Blocked" value={stats.blocked} icon={HiOutlineBan} />
                  </div>
                )}
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4 text-sm"
                >
                  {error}
                </motion.div>
              )}

              {/* Mobile Card View */}
              <div className="block lg:hidden">
                <AnimatePresence mode="wait">
                  {filteredUsers.length === 0 ? (
                    renderEmptyState()
                  ) : (
                    <motion.div
                      key="content"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="space-y-3"
                    >
                      {currentData.map((user, index) => (
                        <motion.div
                          key={user._id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                                <HiOutlineUser className="w-5 h-5 text-gray-600" />
                              </div>
                              <div>
                                <h3 className="font-medium text-gray-800">{user.fullName || 'N/A'}</h3>
                                <p className="text-sm text-gray-500 flex items-center gap-1">
                                  <HiOutlineMail className="w-3 h-3" />
                                  {user.email}
                                </p>
                              </div>
                            </div>
                            {getStatusBadge(user.isBlocked)}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3 text-xs text-gray-500 mb-4">
                            <div className="bg-gray-50 p-2 rounded-lg">
                              <span className="block text-gray-400 mb-1 flex items-center gap-1">
                                <HiOutlineCalendar className="w-3 h-3" />
                                Created
                              </span>
                              <span className="text-gray-700">{formatDate(user.createdAt)}</span>
                            </div>
                            <div className="bg-gray-50 p-2 rounded-lg">
                              <span className="block text-gray-400 mb-1 flex items-center gap-1">
                                <HiOutlineClock className="w-3 h-3" />
                                Last Login
                              </span>
                              <span className="text-gray-700">{formatDate(user.lastLogin)}</span>
                            </div>
                          </div>

                          <div className="flex items-center justify-end pt-3 border-t border-gray-100">
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-gray-600">
                                {user.isBlocked ? 'Blocked' : 'Active'}
                              </span>
                              <button
                                onClick={() => handleToggleBlock(user._id, user.isBlocked, user.fullName)}
                                className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 ${
                                  user.isBlocked ? 'bg-gray-300' : 'bg-gray-600'
                                }`}
                                role="switch"
                                aria-checked={!user.isBlocked}
                              >
                                <span
                                  className={`inline-block w-4 h-4 transform transition-transform bg-white rounded-full ${
                                    user.isBlocked ? 'translate-x-1' : 'translate-x-6'
                                  }`}
                                />
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <AnimatePresence mode="wait">
                      {filteredUsers.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="px-6 py-12">
                            {renderEmptyState()}
                          </td>
                        </tr>
                      ) : (
                        currentData.map((user, index) => (
                          <motion.tr
                            key={user._id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.03 }}
                            className="hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                                  <HiOutlineUser className="w-4 h-4 text-gray-600" />
                                </div>
                                <span className="text-sm font-medium text-gray-800">
                                  {user.fullName || 'N/A'}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {user.email}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {getStatusBadge(user.isBlocked)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="group relative">
                                <span className="cursor-help border-b border-dotted border-gray-300">
                                  {new Date(user.createdAt).toLocaleDateString()}
                                </span>
                                <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                                  {formatDate(user.createdAt)}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="group relative">
                                <span className="cursor-help border-b border-dotted border-gray-300">
                                  {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                                </span>
                                {user.lastLogin && (
                                  <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                                    {formatDate(user.lastLogin)}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-3">
                                <span className="text-sm text-gray-500">
                                  {user.isBlocked ? 'Blocked' : 'Active'}
                                </span>
                                <button
                                  onClick={() => handleToggleBlock(user._id, user.isBlocked, user.fullName)}
                                  className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 ${
                                    user.isBlocked ? 'bg-gray-300' : 'bg-gray-600'
                                  }`}
                                  role="switch"
                                  aria-checked={!user.isBlocked}
                                  title={`${user.isBlocked ? 'Unblock' : 'Block'} ${user.fullName}`}
                                >
                                  <span
                                    className={`inline-block w-4 h-4 transform transition-transform bg-white rounded-full ${
                                      user.isBlocked ? 'translate-x-1' : 'translate-x-6'
                                    }`}
                                  />
                                </button>
                              </div>
                            </td>
                          </motion.tr>
                        ))
                      )}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {filteredUsers.length > itemsPerPage && (
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-gray-500">
                    Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                    {Math.min(currentPage * itemsPerPage, filteredUsers.length)} of{' '}
                    {filteredUsers.length} users
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <HiOutlineChevronLeft className="w-4 h-4" />
                    </button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                              currentPage === pageNum
                                ? 'bg-gray-700 text-white'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <HiOutlineChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;